import { Component, OnDestroy, Inject, ViewChild } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { navItems } from '../../_nav';
import { SESSION_STORAGE, LOCAL_STORAGE, WebStorageService } from 'ngx-webstorage-service';
import { WhatsNewFacade } from '../../common/facade/WhatsNewFacade';
import { getCurrentWhatsNew, WhatsNewEntry, WhatsNewSections } from '../../common/model/whats-new.data';
import { Permissions } from '../../common/model/dictionary/Permissions';
import { AuthService } from '../../auth.service';
import { Strings } from '../../common/language/strings';
import { res } from '../../../app/common/language/text/res';
import { TenantFacade } from '../../common/facade/TenantFacade';
import { StoreFacade } from '../../common/facade/StoreFacade';
import { Store } from '../../common/model/Store';
import { OrganizationFacade } from '../../common/facade/OrganizationFacade';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-dashboard',
    templateUrl: './default-layout.component.html',
    styleUrls: ['./default-layout.component.css']
})
export class DefaultLayoutComponent implements OnDestroy {
  public navItems = navItems;
  public sidebarMinimized = true;
  public minimizeMenuBar = true;
  private changes: MutationObserver;
  public element: HTMLElement;
  public permissionsList: any[] = [];
  public userName = '';
  public isSpanish = true;
  public cmb_tenant: number;
  public lsTenant = [];
  public version: string = environment.version;
  public isProduction: boolean = environment.production;
  public environmentName: String = environment.name;

  // What's New (novedades de la tanda actual). Se ancla al whatsNewId ESTABLE de la tanda,
  // NO a environment.version (el pipeline reescribe la versión del front en cada build).
  @ViewChild('whatsNewModal') public whatsNewModal!: ModalDirective;
  public whatsNew: WhatsNewEntry | null = getCurrentWhatsNew();
  // true = hay una tanda activa que el usuario aún no ha visto (enciende el badge del foquito).
  public whatsNewUnseen = false;
  private readonly _whatsNewKey = 'whatsnew_seen_' + (this.whatsNew ? this.whatsNew.whatsNewId : '');

  lblLogOut = res.LogOut;
  lblSelectAnOption = res.SelectAnOption;
  lblTenantNotAvailable = res.TenantNotAvailable;
  lblWhatsNewTitle = res.WhatsNewTitle;
  lblWhatsNewLink = res.WhatsNewLink;
  lblWhatsNewAdded = res.WhatsNewAdded;
  lblWhatsNewImproved = res.WhatsNewImproved;
  lblWhatsNewFixed = res.WhatsNewFixed;
  lblWhatsNewGotIt = res.WhatsNewGotIt;
  generalAlerts: any[];
  alerts: any[];
  loading = false;
  messageLoader: string;
  userData: any;
  lsAvailableStores: Store[];
  lsAvailableOrganizations: any[] = [];

  constructor(
    public strings: Strings,
    private authService: AuthService,
    private _TenantFacade: TenantFacade,
    private _OrganizationFacade: OrganizationFacade,
    private _StoreFacade: StoreFacade,
    private _router: Router,
    private _whatsNewFacade: WhatsNewFacade,
    @Inject(SESSION_STORAGE) private _storage: WebStorageService,
    @Inject(LOCAL_STORAGE) private _localStorage: WebStorageService,
    @Inject(DOCUMENT) _document?: any
  ) {
    this.changes = new MutationObserver((mutations) => {
      this.sidebarMinimized = _document.body.classList.contains('sidebar-minimized');
    });
    this.element = _document.body;
    this.changes.observe(<Element>this.element, {
      attributes: true,
      attributeFilter: ['class']
    });

    this.userData = this._storage.get('user');
    this.loadTenants();
    this.loadAvailabeOrganizations();
    this.loadAvailableStores();
    this.validatePermissions();
    this.initWhatsNew();

  }

  /**
   * Novedades de la tanda actual. Solo aplica si hay una tanda marcada para mostrarse.
   * Fuente de verdad del "ya lo vi": BD (por usuario, para auditar adopción). localStorage
   * se usa como caché para no consultar al backend en cada carga de página.
   * El id de la tanda (whatsNewId) es ESTABLE; no depende de environment.version.
   */
  private initWhatsNew() {
    if (!this.whatsNew) { return; }                     // no hay tanda activa
    if (this._localStorage.get(this._whatsNewKey)) {    // ya marcado visto en este navegador
      this.whatsNewUnseen = false;
      return;
    }

    this._whatsNewFacade.GetStatus(this.whatsNew.whatsNewId).subscribe(
      resp => {
        const seen = resp && resp.completed && resp.data ? resp.data.seen : false;
        if (seen) {
          this._localStorage.set(this._whatsNewKey, true); // sincroniza caché
          this.whatsNewUnseen = false;
        } else {
          this.whatsNewUnseen = true;   // enciende el badge del foquito
          this.openWhatsNew();          // y además abre el modal automático (primera vez)
        }
      },
      () => { /* si falla la consulta, no bloquear la app: no se muestra */ }
    );
  }

  /** Abre el modal de novedades (también reutilizable desde el link "Novedades"). */
  openWhatsNew() {
    if (!this.whatsNew) { this.whatsNew = getCurrentWhatsNew(); }
    if (this.whatsNew && this.whatsNewModal) {
      this.whatsNewModal.show();
    }
  }

  /** Cierra el modal y marca la tanda como vista (BD + caché local). Apaga el badge. */
  closeWhatsNew() {
    if (this.whatsNewModal) { this.whatsNewModal.hide(); }
    this._localStorage.set(this._whatsNewKey, true);
    this.whatsNewUnseen = false;
    if (!this.whatsNew) { return; }
    this._whatsNewFacade.MarkSeen(this.whatsNew.whatsNewId).subscribe(
      () => { /* best-effort: la auditoría queda en BD */ },
      () => { /* si falla, el caché local evita que reaparezca en este navegador */ }
    );
  }

  /** Título/fecha/secciones del What's New en el idioma seleccionado (para el template). */
  get whatsNewTitle(): string {
    if (!this.whatsNew) { return ''; }
    return this.isSpanish ? this.whatsNew.title.es : this.whatsNew.title.en;
  }
  get whatsNewDate(): string {
    if (!this.whatsNew) { return ''; }
    return this.isSpanish ? this.whatsNew.date.es : this.whatsNew.date.en;
  }
  get whatsNewSections(): WhatsNewSections | null {
    if (!this.whatsNew) { return null; }
    return this.isSpanish ? this.whatsNew.es : this.whatsNew.en;
  }

  changeSideBar() {
    this.sidebarMinimized = this.element.classList.contains('sidebar-minimized');
    if (this.sidebarMinimized) {
      this.element.classList.remove('sidebar-minimized');
      this.element.classList.add('brand-minimized');
    } else {
      this.element.classList.add('sidebar-minimized');
      this.element.classList.remove('brand-minimized');
    }
  }
  // Validar permisos para mostrar secciones en las que se puede navegar
  validatePermissions() {
    this.permissionsList = this._storage.get('permissions');
    this.userName = this._storage.get('user').name.toString().toUpperCase();

    const language = this._storage.get('catDetLanguageId');

    if (language == 123) {
      this.isSpanish = true;
    } else {
      this.isSpanish = false;
    }

    

    let index = -1;
    let visible = false;
    index = navItems.findIndex(item => item.name === 'Reporte Actividades');
    if (index !== -1) {
        this.permissionsList.forEach(element => {
            if (element.name === Permissions.RptActivityGeneric
                || element.name === Permissions.RptActivityLevelChange
                || element.name === Permissions.RptActivityOpenClose
                || element.name === Permissions.RptActivityPriceChange
                || element.name === Permissions.GeneralAccess
                || element.name === Permissions.RptActivityVIM
                || element.name === Permissions.RptActivityCoord
                || element.name === Permissions.RptActivityAudit
            ) {
                navItems[index].name = this.strings.GetText(res.ActivitiesReport);
                visible = true;
            }
        });
        if (!visible) {
            navItems.splice(index, 1);
        }
    }

 

    index = -1;
    visible = false;

    let nItemRepTienda = navItems.find(item => item.name == 'Reporte Tiendas');
    let indexParentRepTienda = navItems.findIndex(item => item.name == 'Reporte Tiendas');

    index = nItemRepTienda.children.findIndex(child => child.name === 'Tiendas')
    if (index !== -1) {
        this.permissionsList.forEach(element => {
            if (element.name === Permissions.GeneralAccess
              || element.name === Permissions.RptStoreRead
            ) {
                navItems[indexParentRepTienda].children[index].name = this.strings.GetText(res.StoresReport);
                visible = true;
            }
        });
        if (!visible) {
            navItems[indexParentRepTienda].children.splice(index, 1);
        }
    }
    /*
    index = nItemRepTienda.children.findIndex(child => child.name === 'CheckList')
    if (index !== -1) {
        this.permissionsList.forEach(element => {
            if (element.name === Permissions.GeneralAccess
              || element.name === Permissions.RptActivityChklist
            ) {
                navItems[indexParentRepTienda].children[index].name = this.strings.GetText(res.StoresReportChecklist);
                visible = true;
            }
        });
        if (!visible) {
            navItems[indexParentRepTienda].children.splice(index, 1);
        }
    }
    */

        index = -1;
        visible = false;
        index = navItems.findIndex(item => item.name === 'Acciones Correctivas');
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.GeneralAccess
                    || element.name === Permissions.RptActivityVIM
                    || element.name === Permissions.RptActivityCoord
                    || element.name === Permissions.RptActivityAudit
                    || element.name === Permissions.RptActivitySupervisor
                    || element.name === Permissions.CorrectiveActionsRead
                ) {
                    navItems[index].name = this.strings.GetText(res.CorrectiveActions);
                    visible = true;
                }
            });
            if (!visible) {
                navItems.splice(index, 1);
            }
        }

        index = -1;
        visible = false;
        index = navItems.findIndex(item => item.name === 'Programar Actividades');
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.AdminChecklistGenericRead
                    || element.name === Permissions.AdminChecklistGenericCreate
                    || element.name === Permissions.AdminChecklistGenericEdit
                    || element.name === Permissions.AdminChecklistLevelChangeRead
                    || element.name === Permissions.AdminChecklistLevelChangeCreate
                    || element.name === Permissions.AdminChecklistLevelChangeEdit
                    || element.name === Permissions.AdminChecklistOpenCloseRead
                    || element.name === Permissions.AdminChecklistOpenCloseCreate
                    || element.name === Permissions.AdminChecklistOpenCloseEdit
                    || element.name === Permissions.GeneralAccess
                ) {
                    navItems[index].name = this.strings.GetText(res.ScheduleActivities);
                    visible = true;
                }
            });
            if (!visible) {
                navItems.splice(index, 1);
            }
        }

        index = -1;
        visible = false;
        index = navItems.findIndex(item => item.name === 'Administrar Plantillas');
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.PlantillasComplete
                    || element.name === Permissions.PlantillasCreate
                    || element.name === Permissions.PlantillasEdit
                    || element.name === Permissions.PlantillasRead
                    || element.name === Permissions.GeneralAccess
                ) {
                    navItems[index].name = this.strings.GetText(res.TemplateManager);
                    visible = true;
                }
            });
            if (!visible) {
                navItems.splice(index, 1);
            }
        }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Ingreso de Mercancía');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.MerchIncomeCreate
          || element.name === Permissions.MerchIncomeEdit
          || element.name === Permissions.MerchIncomeRead) {
            navItems[index].name = this.strings.GetText(res.MerchandiseEntry);
            visible = true;
        }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Recepciones');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.ReceptionCreate
          || element.name === Permissions.ReceptionEdit
          || element.name === Permissions.ReceptionRead) {
            navItems[index].name = this.strings.GetText(res.Receptions);
            visible = true;
          }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Traspasos Corporativo');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.AdminTraspasoCorpoCreate
          || element.name === Permissions.AdminTraspasoCorpoEdit
          || element.name === Permissions.AdminTraspasoCorpoRead) {
            navItems[index].name = this.strings.GetText(res.CorporateTransfer);
            visible = true;
        }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Traspasos Tienda');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.AdminTraspasoStoreCreate
          || element.name === Permissions.AdminTraspasoStoreoRead) {
            navItems[index].name = this.strings.GetText(res.StoreTransfers);
            visible = true;
        }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

        index = -1;
        visible = false;

        let nItem = navItems.find(item => item.name == 'Inventario');
        let indexParent = navItems.findIndex(item => item.name == 'Inventario');

        index = nItem.children.findIndex(child => child.name === 'Físico')
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.GeneralAccess
                    || element.name === Permissions.InventarioConteoRead
                    || element.name === Permissions.InventarioFisicoRead
                    || element.name === Permissions.InventarioConteoCreate
                    || element.name === Permissions.InventarioConteoEdit
                    || element.name === Permissions.InventarioFisicoCreate
                    || element.name === Permissions.InventarioFisicoEdit) {
                    navItems[indexParent].children[index].name = this.strings.GetText(res.Physical);
                    visible = true;
                }
            });
            if (!visible) {
                navItems[indexParent].children.splice(index, 1);
            }
        }

        index = -1;
        visible = false;
        index = nItem.children.findIndex(child => child.name === 'Pre-planchado');
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.PreIronedRead
                    || element.name === Permissions.PreIronedLoad
                    || element.name === Permissions.PreIronedDownload) {
                    navItems[indexParent].children[index].name = this.strings.GetText(res.PreIroned);
                    visible = true;
                }
            });

            if (!visible) {
                navItems[indexParent].children.splice(index, 1);
            }
        }

        if(nItem.children != null && nItem.children.length == 0) {
          navItems.splice(indexParent, 1);
        }

        index = -1;
        visible = false;

        nItem = navItems.find(item => item.name == 'Devoluciones');
        indexParent = navItems.findIndex(item => item.name == 'Devoluciones');

        index = nItem.children.findIndex(child => child.name === 'Devoluciones Programadas')
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.GeneralAccess
                    || element.name === Permissions.DevolucionesRead
                    || element.name === Permissions.DevolucionesCreate
                    || element.name === Permissions.DevolucionesEdit
                    || element.name === Permissions.DevolucionesComplete
                ) {
                    navItems[indexParent].children[index].name = this.strings.GetText(res.CorporateReturns);
                    visible = true;
                }
            });
            if (!visible) {
                navItems[indexParent].children.splice(index, 1);
            }
        }

        index = -1;
        visible = false;
        index = nItem.children.findIndex(child => child.name === 'Devoluciones por Defecto')
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.GeneralAccess
                    || element.name === Permissions.DevolucionesRead
                    || element.name === Permissions.DevolucionesCreate
                    || element.name === Permissions.DevolucionesEdit
                    || element.name === Permissions.DevolucionesComplete
                ) {
                    navItems[indexParent].children[index].name = this.strings.GetText(res.Returns);
                    visible = true;
                }
            });
            if (!visible) {
                navItems[indexParent].children.splice(index, 1);
            }
        }

        index = -1;
        visible = false;
        index = nItem.children.findIndex(child => child.name === 'Solicitudes de Devolución por Defecto')
        if (index !== -1) {
            this.permissionsList.forEach(element => {
                if (element.name === Permissions.GeneralAccess
                    || element.name === Permissions.DevolucionesCreate
                    || element.name === Permissions.DevolucionesEdit
                    || element.name === Permissions.DevolucionesComplete
                ) {
                    navItems[indexParent].children[index].name = this.strings.GetText(res.ReturnsRequests);
                    visible = true;
                }
            });
            if (!visible) {
                navItems[indexParent].children.splice(index, 1);
            }
        }

        if(nItem.children != null && nItem.children.length == 0) {
          navItems.splice(indexParent, 1);
        }

        index = -1;
        visible = false;
        index = navItems.findIndex(item => item.name === 'Artículos negados para venta');
        if (index !== -1) {
          this.permissionsList.forEach(element => {
            if (element.name === Permissions.GeneralAccess
              || element.name === Permissions.UnsoldItemSizeRead
              ) {
                navItems[index].name = this.strings.GetText(res.UnsoldItemSize);
                visible = true;
              }
          });
          if (!visible) {
            navItems.splice(index, 1);
          }
        }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Administrar Tiendas');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.AdminStoreCreate
          || element.name === Permissions.AdminStoreEdit
          || element.name === Permissions.AdminStoreRead
          || element.name === Permissions.StoreAdminAddressCreate
          || element.name === Permissions.StoreAdminAddressEdit
          || element.name === Permissions.StoreAdminAudSupVimCoorCreate
          || element.name === Permissions.StoreAdminAudSupVimCoorEdit) {
            navItems[index].name = this.strings.GetText(res.ManageStores);
            visible = true;
          }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Admin. Dispositivos');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.AdminDevicesCreate
          || element.name === Permissions.AdminDevicesEdit
          || element.name === Permissions.AdminDevicesRead) {
            navItems[index].name = this.strings.GetText(res.ManageDevicesAbbr);
            visible = true;
        }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Coleccionador de imagenes');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.ImageCollectorCreate
          || element.name === Permissions.ImageCollectorEdit
          || element.name === Permissions.ImageCollectorRead) {
            navItems[index].name = this.strings.GetText(res.ImageCollector);
            visible = true;
          }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Admin. Tiendas por usuario');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.ConfigPositionRead
          || element.name === Permissions.GeneralAccess) {
            navItems[index].name = this.strings.GetText(res.OrganizationalStructure);
            visible = true;
          }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Admin. Empleados');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.AdminEmployeesCreate
          || element.name === Permissions.AdminEmployeesEdit
          || element.name === Permissions.AdminEmployeesRead) {
            navItems[index].name = this.strings.GetText(res.ManageEmployeesAbbr);
            visible = true;
          }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

    index = -1;
    visible = false;
    index = navItems.findIndex(item => item.name === 'Admin. Zonas');
    if (index !== -1) {
      this.permissionsList.forEach(element => {
        if (element.name === Permissions.GeneralAccess
          || element.name === Permissions.ZonasVisualCreate
          || element.name === Permissions.ZonasVisualEdit
          || element.name === Permissions.ZonasVisualRead
          || element.name === Permissions.ZonasSupervisorCreate
          || element.name === Permissions.ZonasSupervisorEdit
          || element.name === Permissions.ZonasSupervisorRead
          || element.name === Permissions.ZonasAuditorRead
          || element.name === Permissions.ZonasAuditorEdit
          || element.name === Permissions.ZonasAuditorCreate) {
            navItems[index].name = this.strings.GetText(res.ManageZonesAbbr);
            visible = true;
        }
      });
      if (!visible) {
        navItems.splice(index, 1);
      }
    }

      index = -1;
      visible = false;
      index = navItems.findIndex(item => item.name === 'Admin. Módulos');
      if (index !== -1) {
          this.permissionsList.forEach(element => {
              if (element.name === Permissions.GeneralAccess) {
                  navItems[index].name = this.strings.GetText(res.ManageModulesMenu);
                  visible = true;
              }
          });
          if (!visible) {
              navItems.splice(index, 1);
          }
      }

      index = -1;
      visible = false;
      index = navItems.findIndex(item => item.name === 'Restock');
      if (index !== -1) {
          this.permissionsList.forEach(element => {
              if (element.name === Permissions.GeneralAccess
                  || element.name === Permissions.RestockLayout
              ) {
                  navItems[index].name = this.strings.GetText(res.Restock);
                  visible = true;
              }
          });
          if (!visible) {
              navItems.splice(index, 1);
          }
      }

      index = -1;
      visible = false;
      index = navItems.findIndex(item => item.name === 'Restock Report');
      if (index !== -1) {
          this.permissionsList.forEach(element => {
              if (element.name === Permissions.GeneralAccess
                  || element.name === Permissions.RestockReport
              ) {
                  navItems[index].name = this.strings.GetText(res.RestockReport);
                  visible = true;
              }
          });
          if (!visible) {
              navItems.splice(index, 1);
          }
      }    

      index = -1;
      visible = false;
      index = navItems.findIndex(item => item.name === 'Diseño de Etiquetas');
      if (index !== -1) {
        this.permissionsList.forEach(element => {
          if (element.name === Permissions.GeneralAccess) {
              navItems[index].name = this.strings.GetText(res.LabelDesign);
              visible = true;
          }
        });
        if (!visible) {
          navItems.splice(index, 1);
        }
      }

      index = -1;
      visible = false;

      index = navItems.findIndex(item => item.name === 'Indicadores');
      if(index !== -1) {
        navItems[index].name = this.strings.GetText(res.Indicators);
      }      
      
      index = -1;
      index = navItems.findIndex(item => item.name === 'Operación');
      if(index !== -1) {
        navItems[index].name = this.strings.GetText(res.Operation);
      }
      
      index = -1;
      index = navItems.findIndex(item => item.name === 'Inventario');
      if(index !== -1) {
        navItems[index].name = this.strings.GetText(res.Inventory);
      }

      index = -1;
      index = navItems.findIndex(item => item.name === 'Devoluciones');
      if(index !== -1) {
        navItems[index].name = this.strings.GetText(res.Returns);
      }

      index = -1;
      index = navItems.findIndex(item => item.name === 'Configuración');
      if(index !== -1) {
        navItems[index].name = this.strings.GetText(res.Configuration);
      }

      /*
      index = -1;
      visible = false;
      index = navItems.findIndex(item => item.name === 'Facturación');
      if (index !== -1){
        this.permissionsList.forEach(element => {
          if (element.name === Permissions.GeneralAccess) {
            navItems[index].name = this.strings.GetText(res.Invoicement);
            visible = true;
          } 
        });
        if (!visible) {
          navItems.splice(index, 1)
        }
      }
      */
      
      
      index = -1;
      visible = false;
      index = navItems.findIndex(item => item.name === 'Logger Layouts');
      if (index !== -1){
        this.permissionsList.forEach(element => {
          if (element.name === Permissions.LogLayoutAccess) {
            navItems[index].name = this.strings.GetText(res.LoggerLayout);
            visible = true;
          } 
        });
        if (!visible) {
          navItems.splice(index, 1)
        }
      }

      index = -1;
      visible = false;

      
      nItem = navItems.find(item => item.name == 'Admin. Usuarios');
      indexParent = navItems.findIndex(item => item.name == 'Admin. Usuarios');

      index = nItem.children.findIndex(child => child.name === 'Admin. Usuarios');
      if (index !== -1) {
        const adminUsersPermissions = [
          Permissions.AdminUsersRead,
          Permissions.AdminUsersCreate,
          Permissions.AdminUsersEdit,
          Permissions.AdminUsersEditRoles
        ];
        if (adminUsersPermissions.some(p => this.permissionsList.some(pl => pl.name === p))) {
          navItems[indexParent].children[index].name = this.strings.GetText(res.UsersAdmin);
        } else {
          navItems[indexParent].children.splice(index, 1);
        }
      }

      index = nItem.children.findIndex(child => child.name === 'Admin. Roles');
      if (index !== -1) {
        const adminRolePermissions = [
          Permissions.AdminRolePermissionsRead,
          Permissions.AdminRolePermissionsEdit
        ];
        if (adminRolePermissions.some(p => this.permissionsList.some(pl => pl.name === p))) {
          navItems[indexParent].children[index].name = this.strings.GetText(res.AdminRoles);
        } else {
          navItems[indexParent].children.splice(index, 1);
        }
      }

      if (navItems[indexParent].children.length > 0) {
        navItems[indexParent].name = this.strings.GetText(res.UsersAdmin);
      } else {
        navItems.splice(indexParent, 1);
      }
    }

    

    ngOnDestroy(): void {
        this.changes.disconnect();
    }

    closeSession() {
        this.authService.logout();
    }

  changeLanguage(language: number) {
    this._storage.set('catDetLanguageId', language);
    this.isSpanish = !this.isSpanish;
    location.reload();
  }

  loadTenants() {
    this.loading = true;
    this.messageLoader = this.strings.GetText(res.LoadingUserData);
    this._TenantFacade.GetTenantByEmail(this.userData.email).subscribe(
      response => {
        this.loading = false;
        if (response.completed) {
          if (response.data.length > 0) {
              this.lsTenant = response.data;
              if (this.lsTenant.length > 0) {
                  this._storage.set('availableTenants', this.lsTenant);
                  if (this._storage.get('tenantId') == undefined) {
                  this.cmb_tenant = this.lsTenant[0].tenantId;
                  this._storage.set('tenantId',this.lsTenant[0].tenantId);
                } else {
                  this.cmb_tenant = this._storage.get('tenantId');
                }
                
              }
          } else {
            this.addAlert(this.strings.GetText(res.AlertThisUserDoesNotBelongATenantAvailable), 'danger');
          }
        } else {
          this.addAlert('<strong>¡Error!</strong> ' + this.strings.GetText(res.AlertThereWasErrorConsultingTenant), 'danger');
        }
      }, error => {
        this.loading = false;
        this.addAlert('<strong>¡Error!</strong> ' + this.strings.GetText(res.AlertTenantDataCouldNotBeObtained), 'danger');
      }
    );
  }

  loadAvailabeOrganizations() {
    this.loading = true;
    this.messageLoader = this.strings.GetText(res.LoadingUserData);
    this._OrganizationFacade.GetCollectionByEmail(this.userData.email).subscribe(
      response => {
        this.loading = false;
        if (response.completed) {
          if (response.data.length > 0) {
              this.lsAvailableOrganizations = response.data;
              if (this.lsAvailableOrganizations.length > 0) {
                  this._storage.set('availableOrganizations', this.lsAvailableOrganizations);                
              }
          } else {
            this.addAlert(this.strings.GetText(res.AlertThisUserDoesNotBelongATenantAvailable), 'danger');
          }
        } else {
          this.addAlert('<strong>¡Error!</strong> ' + this.strings.GetText(res.AlertThereWasErrorConsultingTenant), 'danger');
        }
      }, error => {
        this.loading = false;
        this.addAlert('<strong>¡Error!</strong> ' + this.strings.GetText(res.AlertTenantDataCouldNotBeObtained), 'danger');
      }
    );
  }

  loadAvailableStores() {
    this.loading = true;
    this.messageLoader = this.strings.GetText(res.LoadingUserData);
    this._StoreFacade.GetCollectionByStoreEmail(this.userData.email).subscribe(
      response => {
        this.loading = false;
        if (response.completed) {
          if (response.data.length > 0) {
              this.lsAvailableStores = response.data;
              this._storage.set('availableStores', this.lsAvailableStores);
          } else {
            this.addAlert(this.strings.GetText(res.AlertThisUserDoesNotBelongATenantAvailable), 'danger');
          }
        } else {
          this.addAlert('<strong>¡Error!</strong> ' + this.strings.GetText(res.AlertThereWasErrorConsultingTenant), 'danger');
        }
      }, error => {
        this.loading = false;
        this.addAlert('<strong>¡Error!</strong> ' + this.strings.GetText(res.AlertTenantDataCouldNotBeObtained), 'danger');
      }
    );
  }


  onChangeTenant() {
    this._storage.set('tenantId', this.cmb_tenant);
      let url = this._storage.get('moduleUrl');
      if (url.includes('?')) {
          url = url.split('?')[0];
      }
    if(url == this._router.url) {
      location.reload();
    }
    else {
      this._router.navigate([url]);
    }
  }

  // añadir alert
  addAlert(message: String, type: String) {
    this.alerts = [];
    this.alerts.push({
      type: type,
      msg: message
    });
  }
}
